import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import Article from '../../components/Article/Article';
import Pagination from '../../components/Pagination/Pagination';
import BreadCrumbs from '../../components/BreadCrumbs/BreadCrumbs';
import FilterSection from '../../components/FilterSection/FilterSection';
import { MetroSpinner } from 'react-spinners-kit';
import Select, { StylesConfig } from 'react-select';

import DiseaseSymptomInput from '../../components/DiseaseSymptomInput/DiseaseSymptomInput';
import SearchLifestyleIntervention from '../../components/SearchLifestyleIntervention/SearchLifestyleIntervention';

import { useService } from 'react-service-locator';
import { SearchService, SortEnum } from '../../services/search.service';
import { LifestyleInterventionService } from '../../services/lifestyle-intervention.service';
import { DiseaseService } from '../../services/disease.service';

import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronLeft } from '../../images/icons/chevron-left.svg';
import { ReactComponent as ChevronDown } from '../../images/icons/chevron-down.svg';

import './ResultPage.scss';

const getNumberWithCommas = (value: number) => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const customSelectStyles: StylesConfig<{ label: string; value: string; disabled: boolean }> = {
    control: (base, state) => ({
        ...base,
        minHeight: 48,
        height: 48,
        borderRadius: 8,
        borderWidth: 2,
        borderColor: state.isDisabled ? '#D7DCE8' : state.isFocused ? '#3A58A1' : '#B9C3DA',
        backgroundColor: state.isDisabled ? '#E9EDF6' : '#FFFFFF',
        boxShadow: 'none',
        ':hover': { borderColor: state.isDisabled ? '#D7DCE8' : '#3A58A1' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 12px' }),
    placeholder: (base, state) => ({
        ...base,
        color: state.isDisabled ? '#A7B1C7' : '#8E99B7',
        fontWeight: 500,
    }),
    singleValue: (base) => ({ ...base, color: '#0B2566', fontWeight: 600 }),
    input: (base) => ({ ...base, color: '#0B2566', fontWeight: 600, margin: 0, padding: 0 }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
        ...base,
        paddingRight: 12,
        paddingLeft: 4,
        color: state.isDisabled ? '#A7B1C7' : '#3A58A1',
    }),
    menu: (base) => ({ ...base, zIndex: 5000, borderRadius: 8, overflow: 'hidden' }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#EDF1FA' : state.isFocused ? '#F4F6FB' : 'transparent',
        color: state.isDisabled ? '#8E99B7' : '#0B2566',
        fontWeight: state.isDisabled ? 500 : 600,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    }),
};

const ResultPage: React.FC = () => {
    const { t } = useTranslation();
    const searchService = useService(SearchService);
    const diseaseService = useService(DiseaseService);
    const lifestyleInterventionService = useService(LifestyleInterventionService);

    const [isScrolled, toggleScrolled] = useState(true);
    const [isEditing, setIsEditing] = useState(false);

    const contentRef = useRef<HTMLDivElement>(null);

    // --- Snapshot to support "Annuleren" ---
    const snapshotRef = useRef<{
        disease: string;
        subcategories: string[];
        symptoms: string[];
        lifestyleInterventions: string[];
        lifestyleInterventionGroup?: string | null;
        sort: SortEnum;
    } | null>(null);

    const onScroll = () => toggleScrolled(window.scrollY <= 100);

    const sortOptions: { label: string; value: SortEnum }[] = useMemo(
        () => [
            { label: t('pages.result-page.sort.score-decrease') as string, value: 'score_desc' },
            { label: t('pages.result-page.sort.score-increase') as string, value: 'score_asc' },
        ],
        [t],
    );

    const toMutable = <T,>(xs: ReadonlyArray<T> | T[] | null | undefined): T[] => (xs ? Array.from(xs) : []);

    // Hardcoded: Subcategory -> allowed Symptom values (exact display strings)
    const CARDIO_MAP: Record<string, string[]> = {
        'heart disease': [
            'Angina pectoris',
            'Arteriosclerosis',
            'Atrium fibrillation',
            'Cardiomyopathies',
            'Coronary arteries',
            'Coronary microcirculation',
            'Endocarditis',
            'Heart attack',
            'Heart failure',
            'Heart rhythm disorder',
            'Heart valve disease',
            'Myocarditis',
            'Pericarditis',
            'Pulmonate hypertension',
        ],
        'vascular disease': ['Aneurysm', 'Narrowing carotid artery', 'SCAD', 'Thrombosis'],
        'cerebral disease': [
            'Cerebral infarction',
            'CVA',
            'Cerebral Vascular Accident',
            'Stroke',
            'TIA',
            'Transient Ischaemic Attack',
        ],
    };

    // Current selections (mutable copies)
    const selectedSubcats = toMutable(searchService.state.subcategories);
    const selectedSyms = toMutable(searchService.state.symptoms);

    const allowedSymptomSet = React.useMemo(() => {
        if (!selectedSubcats.length) return null; // no subcat selected → show all

        // take union across the selected CV subcats we know about
        const out = new Set<string>();
        for (const sc of selectedSubcats) {
            const list = CARDIO_MAP[sc.toLowerCase()];
            if (Array.isArray(list)) list.forEach((v) => out.add(v));
        }

        // if none of the selected subcats are in our CV map, don't filter
        return out.size ? out : null;
    }, [selectedSubcats]);

    // Build base symptom options from your service (strings)
    const allSymptomOptionsRaw = React.useMemo(
        () =>
            (diseaseService.state.symptoms ?? []).map((c: string) => ({
                value: c,
                label: c,
                disabled: false,
            })),
        [diseaseService.state.symptoms],
    );

    const filteredSymptomOptions = React.useMemo(() => {
        if (!allowedSymptomSet) return allSymptomOptionsRaw; // show all when no filter
        return allSymptomOptionsRaw.filter((opt) => allowedSymptomSet.has(opt.value));
    }, [allowedSymptomSet, allSymptomOptionsRaw]);

    React.useEffect(() => {
        if (!allowedSymptomSet) return;
        const pruned = selectedSyms.filter((s) => allowedSymptomSet.has(s));
        if (pruned.length !== selectedSyms.length) {
            searchService.setSymptoms([...pruned]); // pass mutable
        }
    }, [allowedSymptomSet]);

    useEffect(() => {
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        if (isEditing) {
            contentRef.current?.setAttribute('inert', '');
            document.body.classList.add('editing-lock'); // optional: prevent background scroll
        } else {
            contentRef.current?.removeAttribute('inert');
            document.body.classList.remove('editing-lock');
        }
        return () => {
            contentRef.current?.removeAttribute('inert');
            document.body.classList.remove('editing-lock');
        };
    }, [isEditing]);

    useEffect(() => {
        // Ensure LI options are present (for editor)
        lifestyleInterventionService.fetchLifestyleInterventions();
    }, [lifestyleInterventionService]);

    const lifestyleInterventionsAll = [
        ...searchService.state.symptoms,
        searchService.state.lifestyleInterventionGroup,
        ...searchService.state.lifestyleInterventions,
    ].filter(Boolean) as string[];

    const openEditor = () => {
        snapshotRef.current = {
            disease: searchService.state.disease!,
            subcategories: [...(searchService.state.subcategories ?? [])],
            symptoms: [...(searchService.state.symptoms ?? [])],
            lifestyleInterventions: [...(searchService.state.lifestyleInterventions ?? [])],
            lifestyleInterventionGroup: searchService.state.lifestyleInterventionGroup,
            sort: searchService.state.sort,
        };
        setIsEditing(true);
    };

    const cancelEditor = () => {
        const snap = snapshotRef.current;
        if (snap) {
            searchService.setSubcategories(snap.subcategories);
            searchService.setSymptoms(snap.symptoms);
            if (typeof (searchService as any).setDisease === 'function') {
                (searchService as any).setDisease(snap.disease);
            }
            if (typeof (searchService as any).setLifestyleInterventions === 'function') {
                (searchService as any).setLifestyleInterventions(snap.lifestyleInterventions);
            }
            if (typeof (searchService as any).setLifestyleInterventionGroup === 'function') {
                (searchService as any).setLifestyleInterventionGroup(snap.lifestyleInterventionGroup ?? null);
            }
            if (typeof (searchService as any).setSort === 'function') {
                (searchService as any).setSort(snap.sort);
            }
        }
        setIsEditing(false);
    };

    // Submit: run search with the *current* service state and collapse
    const submitEditor = async () => {
        await searchService.search(); // no route change; results update in place
        setIsEditing(false);
    };

    if (searchService.shouldRedirect && !isEditing && !searchService.state.result && !searchService.state.isLoading) {
        // Keep this behavior (e.g., when no valid query) — otherwise we never navigate here.
        return <Redirect to="/" />;
    }

    if (!searchService.state.result || searchService.state.isLoading) {
        return (
            <div id="resultpage" className="result wrapper">
                <MetroSpinner size={150} color="#3a58a1" />
            </div>
        );
    }

    return (
        <div id="resultpage" className="result">
            <div className="breadcrumbs-bar">
                <BreadCrumbs
                    breadcrumbs={[
                        { to: '/', name: t('search') },
                        {
                            to: '/results',
                            name: `${searchService.state.disease} ${t('search-results').toLocaleLowerCase()}`,
                        },
                    ]}
                />
            </div>
            {isEditing && <div className="page-dim" aria-hidden="true" />}
            {isEditing && (
                <div
                    className="result-card result-editor"
                    role="region"
                    aria-label={t('pages.result-page.edit-search')}
                >
                    <div className="disease-symptom-row">
                        <div className="field">
                            <DiseaseSymptomInput />
                        </div>

                        <div className="field">
                            <p
                                className={`label body body-normal${
                                    diseaseService.state.symptoms && diseaseService.state.symptoms.length !== 0
                                        ? ''
                                        : ' disabled'
                                }`}
                            >
                                {t('pages.search-page.subcategory')}
                            </p>
                            <div className="select-wrap">
                                <Select
                                    className="select body body-normal"
                                    classNamePrefix="rs"
                                    value={searchService.state.subcategories.map((s: string) => ({
                                        label: s,
                                        value: s,
                                        disabled: false,
                                    }))}
                                    isMulti
                                    options={
                                        diseaseService.state.subcategories?.map((c: string) => ({
                                            value: c,
                                            label: c,
                                            disabled: false,
                                        })) ?? []
                                    }
                                    onChange={(v) => searchService.setSubcategories(v?.map((s) => s.value))}
                                    isDisabled={
                                        !diseaseService.state.subcategories ||
                                        diseaseService.state.subcategories.length === 0
                                    }
                                    placeholder={t('components.search-subcategory.select')}
                                    styles={customSelectStyles}
                                    components={{ DropdownIndicator: ChevronDown as any }}
                                />
                            </div>
                        </div>

                        <div className="field">
                            <p
                                className={`label body body-normal${
                                    diseaseService.state.symptoms && diseaseService.state.symptoms.length !== 0
                                        ? ''
                                        : ' disabled'
                                }`}
                            >
                                {t('pages.search-page.symptom')}
                            </p>
                            <div className="select-wrap">
                                <Select
                                    className="select body body-normal"
                                    classNamePrefix="rs"
                                    value={toMutable(searchService.state.symptoms).map((s: string) => ({
                                        label: s,
                                        value: s,
                                        disabled: false,
                                    }))}
                                    isMulti
                                    options={filteredSymptomOptions}
                                    onChange={(v) =>
                                        searchService.setSymptoms(Array.from(v ?? [], (s: any) => s.value))
                                    }
                                    isDisabled={
                                        !diseaseService.state.symptoms ||
                                        diseaseService.state.symptoms.length === 0 ||
                                        filteredSymptomOptions.length === 0
                                    }
                                    placeholder={t('components.search-symptom.select')}
                                    styles={customSelectStyles}
                                    components={{ DropdownIndicator: ChevronDown as any }}
                                    // Safety: if options get widened elsewhere, keep disallowed ones unselectable while CV filter is active
                                    isOptionDisabled={(opt) => !!allowedSymptomSet && !allowedSymptomSet.has(opt.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <br></br>
                    <div className="lifestyle-intervention-wrapper">
                        <p className="body body-normal">{t('pages.search-page.lifestyle-intervention')}</p>
                        <SearchLifestyleIntervention />
                    </div>
                    <div className="lifestyle-intervention-wrapper"></div>
                    <div className="btn-row">
                        <div
                            onClick={isEditing ? cancelEditor : openEditor}
                            aria-expanded={isEditing}
                            className="search-edit"
                            style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                alignSelf: 'center',
                            }}
                        >
                            {isEditing ? t('Annuleren') : t('pages.result-page.edit-search')}
                        </div>
                        <button className="search-btn" onClick={submitEditor}>
                            {t('search')}
                        </button>
                    </div>
                </div>
            )}
            <div ref={contentRef} className="page-content" aria-hidden={isEditing}>
                {isScrolled ? (
                    <>
                        <BreadCrumbs
                            key="breadcrumbs-top"
                            breadcrumbs={[
                                { to: '/', name: t('search') },
                                {
                                    to: '/results',
                                    name: `${searchService.state.disease} ${t('search-results').toLocaleLowerCase()}`,
                                },
                            ]}
                        />
                        {!isEditing && (
                            <div className="result-card">
                                <div>
                                    <div className="title-row">
                                        <h1>
                                            {searchService.state.disease} (
                                            {getNumberWithCommas(searchService.state.result.totalCount)})
                                        </h1>
                                    </div>
                                    <div>
                                        {!isEditing && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    paddingLeft: 4,
                                                    gap: 16,
                                                }}
                                            >
                                                <p id="filters" className="body body-big" style={{ margin: 0 }}>
                                                    {lifestyleInterventionsAll.map((v, i) =>
                                                        i === lifestyleInterventionsAll.length - 1 ? (
                                                            v
                                                        ) : (
                                                            <span key={v}>
                                                                {v}&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;
                                                            </span>
                                                        ),
                                                    )}
                                                </p>
                                                <div
                                                    onClick={isEditing ? cancelEditor : openEditor}
                                                    aria-expanded={isEditing}
                                                    className="search-edit"
                                                    style={{
                                                        paddingLeft: 16,
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {isEditing ? t('annuleren') : t('pages.result-page.edit-search')}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="result-card">
                        <BreadCrumbs
                            key="breadcrumbs-top"
                            breadcrumbs={[
                                { to: '/', name: t('search') },
                                {
                                    to: '/results',
                                    name: `${searchService.state.disease} ${t('search-results').toLocaleLowerCase()}`,
                                },
                            ]}
                        />
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 4, gap: 16 }}>
                                <p id="filters" className="body body-big" style={{ margin: 0 }}>
                                    {lifestyleInterventionsAll.map((v, i) =>
                                        i === lifestyleInterventionsAll.length - 1 ? (
                                            v
                                        ) : (
                                            <span key={v}>{v}&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;</span>
                                        ),
                                    )}
                                </p>
                                {!isEditing && (
                                    <div
                                        onClick={isEditing ? cancelEditor : openEditor}
                                        aria-expanded={isEditing}
                                        className="search-edit"
                                        style={{
                                            paddingLeft: 16,
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {isEditing ? t('annuleren') : t('pages.result-page.edit-search')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* --- RESULTS --- */}
                <div className="wrapper">
                    <FilterSection />
                    {searchService.state.result.totalCount === 0 ? (
                        <div className="result-card" tabIndex={-1}>
                            <div className="no-results">
                                <h3>{t('pages.result-page.no-results-text')}</h3>
                                <div className="btn-wrapper">
                                    <div
                                        onClick={isEditing ? cancelEditor : openEditor}
                                        aria-expanded={isEditing}
                                        className="body body-big btn"
                                    >
                                        {t('ok')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="result-card">
                            <div className="info">
                                <p className="body body-big">
                                    {getNumberWithCommas(searchService.state.result.totalCount)}{' '}
                                    {t('pages.result-page.results').toLocaleLowerCase()}
                                </p>
                                <Select
                                    className="body body-big select"
                                    value={sortOptions.find((c) => c.value === searchService.state.sort)}
                                    options={sortOptions}
                                    onChange={(v) => searchService.setSort(v?.value as SortEnum)}
                                    components={{ DropdownIndicator: ChevronDown as any }}
                                />
                            </div>
                            {searchService.state.result?.items?.map((c, i) => (
                                <Article key={'article-' + i} article={c} />
                            ))}
                            <div>
                                <Pagination />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResultPage;
